/**
 * CrateItem Component
 * Individual crate item with deck status and rotation mode indicator
 */

import { memo, useCallback } from 'react';
import { Box, Shuffle, Clock } from 'lucide-react';
import type { CrateSummary } from '../../api/types';

interface CrateItemProps {
  crate: CrateSummary;
  onClick: (crate: CrateSummary) => void;
}

export const CrateItem = memo(function CrateItem({ crate, onClick }: CrateItemProps) {
  const handleClick = useCallback(() => {
    onClick(crate);
  }, [onClick, crate]);

  const usagePercent = ((crate.poolSize - crate.deckRemaining) / crate.poolSize) * 100;

  return (
    <div
      className="
        group relative flex items-start gap-3 p-3 rounded-lg
        cursor-pointer transition-all duration-150
        border border-zinc-800
        hover:bg-zinc-800/50 hover:border-zinc-700
      "
      onClick={handleClick}
    >
      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <Box className="w-5 h-5 text-emerald-500" />
      </div>

      {/* Crate info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-medium text-zinc-100 truncate">
            {crate.name}
          </h3>

          {/* Rotation mode indicator */}
          {crate.rotationMode === 'SHUFFLE' ? (
            <div title="Shuffle mode">
              <Shuffle className="w-3.5 h-3.5 text-zinc-500" />
            </div>
          ) : (
            <div title="FIFO mode">
              <Clock className="w-3.5 h-3.5 text-zinc-500" />
            </div>
          )}
        </div>

        {/* Deck status */}
        <div className="space-y-1.5">
          <p className="text-xs text-zinc-400">
            <span className="font-medium text-emerald-400">
              {crate.deckRemaining}
            </span>
            <span className="text-zinc-600"> / </span>
            <span className="text-zinc-500">{crate.poolSize}</span>
            <span className="text-zinc-600"> remaining</span>
          </p>

          {/* Progress bar */}
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${100 - usagePercent}%` }}
            />
          </div>

          {/* Search depth */}
          <p className="text-xs text-zinc-600">
            Search depth: {crate.searchDepthPct}%
          </p>
        </div>
      </div>
    </div>
  );
});
